import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, ArrowDown, Star } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { api, fileUrl } from "../lib/api";
import { Reveal } from "../components/site/Reveal";
import { Seo } from "../components/site/Seo";
import { ArtistLineup } from "../components/site/ArtistLineup";
import { ParallaxReel } from "../components/site/ParallaxReel";
import { Marquee } from "../components/site/Marquee";
import { Counter } from "../components/site/Counter";
import { HERO_IMAGE, PROCESS_IMAGE, STUDIO } from "../data/site";
import { InkHero } from "../components/site/InkHero";
import { HeroSlideshow } from "../components/site/HeroSlideshow";

const WORKFLOW = [
  { n: "01", t: "Consult", d: "We start with your idea, placement and reference. Every great piece begins with the conversation." },
  { n: "02", t: "Design", d: "Your artist builds a fully custom design around your body and vision — nothing off a wall." },
  { n: "03", t: "Ink", d: "Sterile setup, expert hand. We take the time the work deserves in the chair." },
  { n: "04", t: "Heal", d: "You leave with clear aftercare so the piece reads clean for decades." },
];

export default function Home() {
  const [offset, setOffset] = useState(0);
  const { data: artists = [] } = useQuery({ queryKey: ["artists"], queryFn: async () => (await api.get("/artists")).data });
  const { data: styles = [] } = useQuery({ queryKey: ["styles"], queryFn: async () => (await api.get("/styles")).data });
  const { data: portfolio = [] } = useQuery({ queryKey: ["portfolio"], queryFn: async () => (await api.get("/portfolio")).data });
  const styleMap = Object.fromEntries(styles.map((s) => [s.slug, s.name]));
  const artistMap = Object.fromEntries(artists.map((a) => [a.slug, a.name]));

  useEffect(() => {
    const onScroll = () => setOffset(window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const strip = portfolio.length ? portfolio : [];

  return (
    <>
      <Seo title="Custom Tattoo Studio in Springfield, VA" description="Custom tattoo artistry in Springfield, VA. Discover your artist, explore the work and book your session. Where precision meets prestige." />

      {/* HERO — XODEX style giant wordmark */}
      <section data-testid="hero-section" className="relative min-h-[100svh] flex flex-col justify-between overflow-hidden pt-[72px]">
        <InkHero image={HERO_IMAGE} />

        <div className="relative z-10 flex-1 flex flex-col justify-center max-w-[1700px] w-full mx-auto px-5 sm:px-8 lg:px-12">
          <Reveal><p className="eyebrow mb-8 flex items-center gap-3"><span className="w-8 h-px bg-violet inline-block" />Springfield, VA — Tattoo Studio</p></Reveal>
          <Reveal delay={80}>
            <img src={STUDIO.logo} alt="Obsidian Art & Tattoo Studio" className="logo-ink w-full max-w-[420px] sm:max-w-[560px] lg:max-w-[760px] h-auto" />
          </Reveal>
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <Reveal delay={140} className="lg:col-span-7">
              <p className="display text-3xl sm:text-4xl lg:text-5xl text-white leading-[0.95]">Where precision<br /><span className="text-violet">meets prestige.</span></p>
            </Reveal>
            <Reveal delay={200} className="lg:col-span-5">
              <p className="text-[#C4C4CC] text-base sm:text-lg leading-relaxed mb-6 max-w-md">Custom tattoo artistry created with precision, intention and individuality.</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/artists" data-testid="hero-find-artist" className="group inline-flex items-center justify-center gap-2 bg-violet text-obsidian font-mono text-xs uppercase tracking-[0.18em] font-bold px-7 py-4 rounded-full hover:bg-white transition-colors">
                  Find Your Artist <ArrowUpRight size={15} className="group-hover:rotate-45 transition-transform" />
                </Link>
                <Link to="/portfolio" data-testid="hero-explore-work" className="inline-flex items-center justify-center gap-2 border border-white/30 text-white font-mono text-xs uppercase tracking-[0.18em] px-7 py-4 rounded-full hover:border-violet hover:text-violet transition-colors">
                  Explore the Work
                </Link>
              </div>
            </Reveal>
          </div>
        </div>

        {/* hero image slideshow */}
        <HeroSlideshow items={strip} />
        <div className="absolute bottom-32 right-5 lg:right-12 z-10 hidden sm:flex items-center gap-2 text-[#828290] font-mono text-[0.6rem] uppercase tracking-[0.3em]">Scroll <ArrowDown size={13} className="animate-bounce" /></div>
      </section>

      {/* MARQUEE band */}
      <div className="border-y border-ash bg-charcoal py-4 overflow-hidden">
        <Marquee duration={30}>
          {["Black & Grey", "Fine Line", "Realism", "Traditional", "Anime", "Blackwork", "Polynesian", "Floral", "Lettering", "Custom"].map((t, i) => (
            <span key={i} className="font-mono text-[0.7rem] sm:text-xs uppercase tracking-[0.28em] text-[#B4B4BE] px-6 flex items-center gap-6">{t}<span className="text-violet text-[0.6rem]">◆</span></span>
          ))}
        </Marquee>
      </div>

      {/* [01] MORE THAN INK */}
      <section data-testid="more-than-ink" className="max-w-[1700px] mx-auto px-5 sm:px-8 lg:px-12 py-24 lg:py-36 border-b border-ash">
        <Reveal><p className="eyebrow mb-10">[ 01 ] — More than ink</p></Reveal>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <Reveal delay={60}><h2 className="font-sans font-semibold text-3xl sm:text-4xl lg:text-6xl text-white leading-[1.05] tracking-tight">Every tattoo is a collaboration between artist and client — <span className="text-[#5A5A66]">custom work designed around your vision, your body, your story.</span></h2></Reveal>
          </div>
          <div className="lg:col-span-4 lg:pt-4">
            <Reveal delay={120}>
              <p className="text-[#9E9EA8] leading-relaxed">We don't reproduce flash off a wall. Line, shade and composition are considered until nothing is arbitrary — then rendered by a hand built for the work.</p>
              <Link to="/studio" className="group inline-flex items-center gap-2 mt-8 text-white font-mono text-xs uppercase tracking-[0.2em] link-underline">Inside the studio <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" /></Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* [02] THE REEL — horizontal parallax */}
      <ParallaxReel items={portfolio} styleMap={styleMap} artistMap={artistMap} />

      {/* [03] STYLES / services list */}
      <section data-testid="styles-teaser" className="max-w-[1700px] mx-auto px-5 sm:px-8 lg:px-12 py-24 border-b border-ash">
        <Reveal><p className="eyebrow mb-10">[ 03 ] — Disciplines</p></Reveal>
        <div>
          {styles.map((s, i) => (
            <Reveal key={s.slug}>
              <Link to={`/styles/${s.slug}`} data-testid={`style-chip-${s.slug}`} className="group flex items-center justify-between gap-6 py-6 lg:py-8 border-t border-ash last:border-b">
                <div className="flex items-baseline gap-5 lg:gap-10">
                  <span className="font-mono text-[0.7rem] text-[#5A5A66] tabular-nums">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="display text-4xl lg:text-6xl text-[#B4B4BE] group-hover:text-white group-hover:translate-x-2 transition-all duration-300">{s.name}</h3>
                </div>
                <span className="hidden md:block text-[#828290] text-sm max-w-xs text-right group-hover:text-[#C4C4CC] transition-colors">{s.blurb}</span>
                <ArrowUpRight className="text-[#5A5A66] group-hover:text-violet shrink-0 transition-colors" size={26} />
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* [04] STATS band */}
      <section data-testid="experience-section" className="relative border-b border-ash overflow-hidden">
        <div className="absolute inset-0 opacity-20"><img src={PROCESS_IMAGE} alt="" className="w-full h-full object-cover" /></div>
        <div className="absolute inset-0 bg-obsidian/80" />
        <div className="relative max-w-[1700px] mx-auto px-5 sm:px-8 lg:px-12 py-24">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-16">
            <Reveal><h2 className="display text-6xl lg:text-8xl text-white">The numbers<br />don't lie</h2></Reveal>
            <Reveal delay={60}>
              <div className="flex items-center gap-3">
                <div className="flex">{[0,1,2,3,4].map((i) => <Star key={i} size={16} className="text-violet fill-violet" />)}</div>
                <span className="font-mono text-xs uppercase tracking-widest text-[#C4C4CC]">Trusted by thousands</span>
              </div>
            </Reveal>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-12 gap-x-8">
            {[
              { el: <><Counter value={artists.length || 11} /></>, l: "Resident artists" },
              { el: "100%", l: "Custom artwork" },
              { el: "Licensed", l: "Experienced team" },
              { el: "Custom", l: "Every piece" },
            ].map((p, i) => (
              <Reveal key={p.l} delay={i * 60}>
                <div className="border-t border-ash pt-5">
                  <div className="display text-5xl lg:text-7xl text-white leading-none">{p.el}</div>
                  <div className="mt-3 font-mono text-[0.6rem] uppercase tracking-[0.2em] text-[#828290]">{p.l}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* [05] ARTISTS — lineup */}
      <section data-testid="featured-artists" className="max-w-[1700px] mx-auto px-5 sm:px-8 lg:px-12 py-24 border-b border-ash">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <Reveal><h2 className="display text-6xl lg:text-8xl text-white">The Hands</h2></Reveal>
          <Reveal delay={60}><Link to="/artists" className="group inline-flex items-center gap-2 text-white font-mono text-xs uppercase tracking-[0.2em] link-underline">Find your artist <ArrowUpRight size={14} className="group-hover:rotate-45 transition-transform" /></Link></Reveal>
        </div>
        <ArtistLineup artists={artists} />
      </section>

      {/* WORKFLOW */}
      <section className="max-w-[1700px] mx-auto px-5 sm:px-8 lg:px-12 py-24 border-b border-ash">
        <Reveal><p className="eyebrow mb-10">[ 06 ] — The process</p></Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-ash border border-ash">
          {WORKFLOW.map((w, i) => (
            <Reveal key={w.n} delay={i * 60} className="bg-obsidian">
              <div className="p-8 h-full hover:bg-charcoal transition-colors">
                <span className="display text-6xl text-violet/30">{w.n}</span>
                <h3 className="display text-3xl text-white mt-4">{w.t}</h3>
                <p className="mt-3 text-[#9E9EA8] text-sm leading-relaxed">{w.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 violet-glow opacity-70" />
        <div className="relative max-w-[1700px] mx-auto px-5 sm:px-8 lg:px-12 py-28 lg:py-44 text-center">
          <Reveal><p className="eyebrow mb-8">Begin</p></Reveal>
          <Reveal delay={60}><h2 className="display text-[16vw] lg:text-[12vw] text-white leading-[0.85]">Ready when<br />you are</h2></Reveal>
          <Reveal delay={140}>
            <Link to="/booking" data-testid="cta-book" className="group inline-flex items-center gap-3 mt-12 bg-violet text-obsidian font-mono text-sm uppercase tracking-[0.2em] font-bold px-10 py-5 rounded-full hover:bg-white transition-colors">
              Book a Session <ArrowUpRight size={17} className="group-hover:rotate-45 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
